// The files on one task: what is attached, who attached it, and adding more.
//
// THREE STEPS TO UPLOAD, AND WHY IT IS NOT ONE
//
//   1. Ask the Worker for a one-shot signed URL. The Worker composes the storage
//      path and checks the task still exists, so neither the path nor the
//      permission is ever the browser's decision.
//   2. PUT the bytes straight to Supabase Storage with that URL.
//   3. Tell the Worker the bytes landed, which is what writes the catalogue row.
//
// The single-call alternative is what the documents dialog does: read the file to
// base64 and post it through the Worker. It works, and it is why that dialog caps
// at 15 MB, because those bytes travel through a Postgres connection on a project
// whose whole pool is sixty connections wide.
//
// The cost of three steps is honest: an upload that dies between 2 and 3 leaves
// an object in the bucket that no row points at. That is invisible and harmless.
// The other ordering, writing the row first, would leave a file in the list that
// nobody can open, which somebody has to notice and report before anyone knows.
//
// THERE IS NO REMOVE CONTROL, AND THAT IS NOT AN OMISSION. Decided on 26 August
// 2026: an attachment on a task is a record. The rule is enforced in three places
// so undoing it has to be deliberate, not accidental: no button here, no route in
// the Worker, and no delete action in the task-asset edge function.
import { useRef, useState } from 'react'
import { ExternalLink, FileText, Image as ImageIcon, Paperclip, Upload } from 'lucide-react'
import { toast } from '../../lib/shadcn/sonner'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import { supabase } from '../../lib/supabase'
import {
  useGetTaskAssetUrl,
  useRecordTaskAsset,
  useSignTaskUpload,
} from '../../hooks/backend/tasks'
import { formatFileSize } from '../../utils/documents'
import { TASK_FILE_MAX_BYTES, oversizeFiles, uploadTaskFiles } from './uploadTaskFiles'
import { longDateTime, type TaskAsset } from '../../utils/tasks'
import { writeErrorMessage } from '../../utils/writeError'

// The three-step upload, the size cap and the reason for both now live in
// uploadTaskFiles.ts, shared with the New task dialog. See that file.

function iconFor(mime: string | null) {
  if (mime && mime.startsWith('image/')) return ImageIcon
  return FileText
}

export function TaskAttachments({
  taskId,
  assets,
  canEdit,
  onChanged,
}: {
  taskId: string
  assets: TaskAsset[]
  canEdit: boolean
  onChanged: () => void
}) {
  const sign = useSignTaskUpload()
  const record = useRecordTaskAsset()
  const getUrl = useGetTaskAssetUrl()
  const inputRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [opening, setOpening] = useState<string | null>(null)

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const chosen = Array.from(files)

    const tooBig = oversizeFiles(chosen)
    if (tooBig.length > 0) {
      toast.error('That file is too large', {
        description: `${tooBig.map((f) => f.name).join(', ')} is over ${Math.round(TASK_FILE_MAX_BYTES / (1024 * 1024))} MB. Compress it, or put a large file in Documents and link to it from an update.`,
      })
      if (inputRef.current) inputRef.current.value = ''
      return
    }

    setBusy(true)
    const { done, failed } = await uploadTaskFiles({
      taskId,
      files: chosen,
      sign: (params) => sign.trigger(params).result as Promise<unknown>,
      record: (params) => record.trigger(params).result as Promise<unknown>,
    })
    setBusy(false)
    if (inputRef.current) inputRef.current.value = ''

    if (failed.length > 0) {
      toast.error(
        done === 0 ? 'Nothing was added' : `${done} of ${chosen.length} added, ${failed.length} did not`,
        { description: failed.join(' | ') },
      )
    } else {
      toast.success(done === 1 ? 'File added' : `${done} files added`)
    }
    // Called even when every file failed, because an earlier file in the same
    // batch can still have landed.
    onChanged()
  }

  async function open(asset: TaskAsset) {
    setOpening(asset.id)
    try {
      const res = (await getUrl.trigger({ id: asset.id }, { skipCache: true }).result) as {
        url: string
      }
      if (!res?.url) throw new Error('No link came back for that file.')
      // A new tab rather than a download attribute: the URL is signed and
      // cross-origin, so the attribute is ignored anyway, and a PDF or an image
      // is usually being looked at rather than saved.
      window.open(res.url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      toast.error('That file could not be opened', { description: writeErrorMessage(err) })
    } finally {
      setOpening(null)
    }
  }

  return (
    <div className="border-b border-border p-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
          <Paperclip className="h-3.5 w-3.5" />
          Files
        </span>
        <span className="text-[11px] tabular-nums text-muted-foreground">{assets.length}</span>
        {canEdit && (
          <div className="ml-auto">
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => void handleFiles(e.target.files)}
            />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 gap-1.5 text-xs"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {busy ? <Spinner className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}
              {busy ? 'Adding' : 'Add a file'}
            </Button>
          </div>
        )}
      </div>

      {/* SAID WHERE THE DECISION IS MADE, not in a policy nobody reads. Everyone
          who can see this board can open everything attached to it, which is all
          ten cockpit logins today. That is deliberate: a file only one person can
          open goes back to WhatsApp, which is what this board replaced. The
          consequence has to be visible at the moment somebody chooses a file. */}
      {canEdit && (
        <p className="mt-2 text-[11px] leading-snug text-muted-foreground">
          Anyone who can see this board can open these files, and nothing here can be removed
          once it is added. A driver's ID, payslip or bank letter belongs in Documents, not on a
          task.
        </p>
      )}

      {assets.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">Nothing attached.</p>
      ) : (
        <ul className="mt-2 space-y-1">
          {assets.map((a) => {
            const Icon = iconFor(a.mime_type)
            const size = formatFileSize(a.size_bytes)
            return (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => void open(a)}
                  disabled={opening === a.id}
                  className={cn(
                    'group flex w-full items-center gap-2 rounded-md border border-border px-2 py-1.5 text-left',
                    'hover:bg-muted disabled:opacity-60',
                  )}
                >
                  {opening === a.id ? (
                    <Spinner className="h-4 w-4 shrink-0" />
                  ) : (
                    <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{a.filename}</span>
                    {/* Who and when, on the row. An attachment that cannot say
                        who added it is not evidence of anything, and this board
                        already carries 18 tasks whose owner is a name with no
                        login. */}
                    <span className="block truncate text-[11px] text-muted-foreground">
                      {a.uploaded_by}
                      {' · '}
                      {longDateTime(a.uploaded_at)}
                      {size ? ` · ${size}` : ''}
                    </span>
                  </span>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-muted-foreground/0 group-hover:text-muted-foreground" />
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
